/**
 * useDebugLog Hook
 * Captura logs del sistema para mostrarlos en UI (útil en móviles sin DevTools)
 */

import { useState, useCallback, useEffect } from 'react';

interface LogEntry {
  id: string;
  timestamp: number;
  level: 'debug' | 'info' | 'warn' | 'error';
  message: string;
  data?: unknown;
}

const MAX_LOGS = 100;
const STORAGE_KEY = 'remote-assist-debug-logs';
const VERBOSE_KEY = 'remote-assist-capture-verbose';
const DEBUG_UI_KEY = 'remote-assist-debug-ui-enabled';
const debugUIListeners = new Set<(v: boolean) => void>();

// Global log store to share across components
class LogStore {
  private logs: LogEntry[] = [];
  private listeners = new Set<(logs: LogEntry[]) => void>();

  constructor() {
    // Load logs from localStorage
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        this.logs = JSON.parse(stored);
      }
    } catch (_e) {
      // ignore
    }
  }

  addLog(level: LogEntry['level'], message: string, data?: unknown) {
    const entry: LogEntry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      timestamp: Date.now(),
      level,
      message,
      data,
    };

    this.logs.push(entry);

    // Keep only last MAX_LOGS entries
    if (this.logs.length > MAX_LOGS) {
      this.logs = this.logs.slice(-MAX_LOGS);
    }

    // Save to localStorage
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.logs.slice(-50))); // Save last 50
    } catch (_e) {
      // ignore storage errors
    }

    // Notify listeners
    // Notify listeners asynchronously to avoid triggering React state updates
    // during another component's render (which causes "setState in render" errors).
    const snapshot = [...this.logs];
    if (typeof queueMicrotask === 'function') {
      queueMicrotask(() => this.listeners.forEach((listener) => listener(snapshot)));
    } else {
      setTimeout(() => this.listeners.forEach((listener) => listener(snapshot)), 0);
    }
  }

  getLogs() {
    return [...this.logs];
  }

  clearLogs() {
    this.logs = [];
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (_e) {
      // ignore
    }
    // Notify listeners asynchronously for the same reason as above.
    const snapshot: LogEntry[] = [];
    if (typeof queueMicrotask === 'function') {
      queueMicrotask(() => this.listeners.forEach((listener) => listener(snapshot)));
    } else {
      setTimeout(() => this.listeners.forEach((listener) => listener(snapshot)), 0);
    }
  }

  subscribe(listener: (logs: LogEntry[]) => void) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}

const logStore = new LogStore();

// Intercept console methods to capture logs.
// By default we only capture `warn` and `error` to avoid noisy logs from
// frequent successful events (mouse/keyboard/webRTC state updates).
// Enable verbose capture by setting localStorage[VERBOSE_KEY] = '1' or
// calling `setCaptureVerbose(true)`.
const originalConsoleLog = console.log;
const originalConsoleInfo = console.info;
const originalConsoleWarn = console.warn;
const originalConsoleError = console.error;
const originalConsoleDebug = console.debug;

let CAPTURE_VERBOSE = false;
try {
  CAPTURE_VERBOSE = localStorage.getItem(VERBOSE_KEY) === '1';
} catch (_e) {
  CAPTURE_VERBOSE = false;
}

export function setCaptureVerbose(enabled: boolean) {
  try {
    if (enabled) localStorage.setItem(VERBOSE_KEY, '1');
    else localStorage.removeItem(VERBOSE_KEY);
  } catch (_e) {
    // ignore
  }
  CAPTURE_VERBOSE = enabled;
}

export function setDebugUIEnabled(enabled: boolean) {
  try {
    if (enabled) localStorage.setItem(DEBUG_UI_KEY, '1');
    else localStorage.removeItem(DEBUG_UI_KEY);
  } catch (_e) {
    // ignore
  }
  // notify listeners
  try {
    debugUIListeners.forEach((l) => {
      try {
        l(enabled);
      } catch (_e) {
        /* ignore listener errors */
      }
    });
  } catch (_e) {}
}

export function getDebugUIEnabled(): boolean {
  try {
    return localStorage.getItem(DEBUG_UI_KEY) === '1';
  } catch (_e) {
    return false;
  }
}

export function subscribeDebugUI(listener: (v: boolean) => void) {
  debugUIListeners.add(listener);
  return () => debugUIListeners.delete(listener);
}

console.log = (...args: unknown[]) => {
  if (!CAPTURE_VERBOSE) return;
  originalConsoleLog(...args);
  logStore.addLog('info', args.map((a) => String(a)).join(' '), args);
};

console.info = (...args: unknown[]) => {
  if (!CAPTURE_VERBOSE) return;
  originalConsoleInfo(...args);
  logStore.addLog('info', args.map((a) => String(a)).join(' '), args);
};

console.warn = (...args: unknown[]) => {
  const stack = new Error().stack;
  try {
    originalConsoleWarn(...args, '\nStack:', stack);
  } catch (_e) {
    try {
      originalConsoleWarn(...args);
    } catch {}
  }
  try {
    logStore.addLog('warn', args.map((a) => String(a)).join(' '), { args, stack });
  } catch (_e) {}
};

console.error = (...args: unknown[]) => {
  const stack = new Error().stack;
  try {
    originalConsoleError(...args, '\nStack:', stack);
  } catch (_e) {
    try {
      originalConsoleError(...args);
    } catch {}
  }
  try {
    logStore.addLog('error', args.map((a) => String(a)).join(' '), { args, stack });
  } catch (_e) {}
};

console.debug = (...args: unknown[]) => {
  if (!CAPTURE_VERBOSE) return;
  originalConsoleDebug(...args);
  logStore.addLog('debug', args.map((a) => String(a)).join(' '), args);
};

export function useDebugLog() {
  const [logs, setLogs] = useState<LogEntry[]>(() => logStore.getLogs());
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState<'all' | 'webrtc' | 'proxy' | 'error'>('all');

  useEffect(() => {
    const unsubscribe = logStore.subscribe(setLogs);
    return () => {
      unsubscribe();
    };
  }, []);

  const clearLogs = useCallback(() => {
    logStore.clearLogs();
  }, []);

  const filteredLogs = logs.filter((log) => {
    if (filter === 'all') return true;
    if (filter === 'error') return log.level === 'error';
    if (filter === 'webrtc')
      return (
        log.message.toLowerCase().includes('webrtc') || log.message.toLowerCase().includes('ice')
      );
    if (filter === 'proxy') return log.message.toLowerCase().includes('proxy');
    return true;
  });

  const exportLogs = useCallback(() => {
    const text = logs
      .map((log) => {
        const date = new Date(log.timestamp).toISOString();
        return `[${date}] [${log.level.toUpperCase()}] ${log.message}`;
      })
      .join('\n');

    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `remote-assist-logs-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }, [logs]);

  return {
    logs: filteredLogs,
    isOpen,
    setIsOpen,
    filter,
    setFilter,
    clearLogs,
    exportLogs,
  };
}
